import abort from '../../../javascripts/utils/abort';

interface RepresentativeImage {
  alt?: string | null;
  copyright?: string | null;
  height?: number | null;
  src: string;
  srcset?: string | null;
  width?: number | null;
}

interface Representative {
  image: RepresentativeImage | null;
  name: string;
  party: string;
  constituency: string | undefined | null;
}

type GeoJsonPolygon = {
  type: 'Polygon';
  coordinates: number[][][];
};

type GeoJsonMultiPolygon = {
  type: 'MultiPolygon';
  coordinates: number[][][][];
};

type ConstituencyGeometry = GeoJsonPolygon | GeoJsonMultiPolygon;

interface ConstituencyResult {
  constituency: {
    name: string;
    number: number;
    state: string;
  };
  geometry?: ConstituencyGeometry | null;
  representatives: Representative[];
  representativesHtml: string;
  warning: string | null;
}

interface ApiResponse {
  constituencies?: ConstituencyResult[];
  success?: boolean;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const POSTAL_CODE_REGEX = /^\d{5}$/;

const renderTemplate = (template: string, vars: Record<string, string | number>): string => template
  .replace(/\{(\w+)\}/g, (_, key: string) => (key in vars ? String(vars[key]) : `{${key}}`));

type Bounds = [[number, number], [number, number]];

const computeBounds = (geometry: ConstituencyGeometry): Bounds => {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  const processCoord = (coord: number[]): void => {
    const [lng, lat] = coord;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  };

  const processRing = (ring: number[][]): void => ring.forEach(processCoord);
  const processPolygon = (polygon: number[][][]): void => polygon.forEach(processRing);

  if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach(processPolygon);
  } else {
    processPolygon(geometry.coordinates);
  }

  return [[minLng, minLat], [maxLng, maxLat]];
};

const createIcon = (spriteUrl: string, name: string): SVGSVGElement => {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', `icon icon--${name}`);
  svg.setAttribute('viewBox', '0 0 200 200');
  svg.setAttribute('role', 'presentation');
  svg.setAttribute('focusable', 'false');

  const use = document.createElementNS(SVG_NS, 'use');
  use.setAttributeNS(XLINK_NS, 'xlink:href', `${spriteUrl}#${name}`);
  svg.appendChild(use);

  return svg;
};

const createNotice = (
  type: 'info' | 'error' | 'success',
  text: string,
  spriteUrl: string,
): { $notice: HTMLDivElement; $text: HTMLSpanElement } => {
  const $notice = document.createElement('div');
  $notice.className = `notice notice--${type}`;

  const $iconWrap = document.createElement('div');
  $iconWrap.className = 'notice__icon';
  $iconWrap.appendChild(createIcon(spriteUrl, type));

  const $text = document.createElement('span');
  $text.className = 'notice__text';
  $text.textContent = text;

  $notice.append($iconWrap, $text);

  return { $notice, $text };
};

type MaplibreModule = typeof import('maplibre-gl');
type MaplibreMap = InstanceType<MaplibreModule['Map']>;

let selectionCounter = 0;

class ConstituencyByPostalCodeElement extends HTMLElement {
  private controller: AbortController | null = null;

  private results: ConstituencyResult[] = [];

  private prefilledConstituencies: number[] = [];

  private embeddedResults: ConstituencyResult[] | null = null;

  private maps: MaplibreMap[] = [];

  private selectionName = '';

  private initialized = false;

  private name = '';

  private spriteUrl = '';

  private textError = '';

  private textMultiple = '';

  private textSelectAll = '';

  private textSuccess = '';

  private textReset = '';

  private tilesBaseUrl = '';

  private $error!: HTMLDivElement;

  private $selection!: HTMLDivElement;

  private $selectionOptions!: HTMLDivElement;

  private $result!: HTMLDivElement;

  private $successText!: HTMLSpanElement;

  private $resultRepresentativesContainer!: HTMLDivElement;

  private $resultWarning!: HTMLDivElement;

  private $resultActions!: HTMLDivElement;

  connectedCallback(): void {
    if (!this.initialized) {
      this.initialize();
      this.initialized = true;
    }

    const $postalCode = this.queryPostalCodeInput();
    $postalCode.addEventListener('input', this.onPostalCodeInput);

    if (this.embeddedResults) {
      this.applyResults(this.embeddedResults);
      this.embeddedResults = null;
    } else if (POSTAL_CODE_REGEX.test($postalCode.value)) {
      this.fetchConstituency($postalCode.value).catch(() => {});
    }
  }

  disconnectedCallback(): void {
    this.controller?.abort();
    this.controller = null;

    this.querySelector<HTMLInputElement>(
      '.constituency-by-postal-code__field input',
    )?.removeEventListener('input', this.onPostalCodeInput);

    this.cleanupMaps();
  }

  private queryPostalCodeInput(): HTMLInputElement {
    return this.querySelector<HTMLInputElement>(
      '.constituency-by-postal-code__field input',
    ) ?? abort();
  }

  private queryMapAttribution(): HTMLElement {
    return this.querySelector<HTMLElement>(
      '.constituency-by-postal-code__map-attribution',
    ) ?? abort();
  }

  private initialize(): void {
    this.name = this.dataset.name ?? '';
    this.spriteUrl = this.dataset.iconSprite ?? '';
    this.textError = this.dataset.textError ?? '';
    this.textMultiple = this.dataset.textMultiple ?? '';
    this.textSelectAll = this.dataset.textSelectAll ?? '';
    this.textSuccess = this.dataset.textSuccess ?? '';
    this.textReset = this.dataset.textReset ?? '';
    this.tilesBaseUrl = this.dataset.tilesBaseUrl ?? '';

    this.prefilledConstituencies = this.readPrefilledConstituencies();
    this.embeddedResults = this.readEmbeddedResults();

    selectionCounter += 1;
    this.selectionName = `constituency-by-postal-code-selection-${selectionCounter}`;

    this.$error = document.createElement('div');
    this.$error.className = 'constituency-by-postal-code__error';
    this.$error.hidden = true;
    this.$error.appendChild(createNotice('error', this.textError, this.spriteUrl).$notice);

    this.$selection = document.createElement('div');
    this.$selection.className = 'constituency-by-postal-code__selection';
    this.$selection.hidden = true;

    const $selectionNotice = document.createElement('div');
    $selectionNotice.className = 'constituency-by-postal-code__selection-notice';
    $selectionNotice.appendChild(createNotice('info', this.textMultiple, this.spriteUrl).$notice);

    this.$selectionOptions = document.createElement('div');
    this.$selectionOptions.className = 'constituency-by-postal-code__selection-options';

    this.$selection.append($selectionNotice, this.$selectionOptions, this.createSelectAllButton());

    this.$result = document.createElement('div');
    this.$result.className = 'constituency-by-postal-code__result';
    this.$result.hidden = true;

    const $successWrap = document.createElement('div');
    $successWrap.className = 'constituency-by-postal-code__success';
    const successNotice = createNotice('success', '', this.spriteUrl);
    this.$successText = successNotice.$text;
    $successWrap.appendChild(successNotice.$notice);

    this.$resultRepresentativesContainer = document.createElement('div');
    this.$resultRepresentativesContainer.className = 'constituency-by-postal-code__representatives';
    this.$resultRepresentativesContainer.hidden = true;

    this.$resultWarning = document.createElement('div');
    this.$resultWarning.className = 'constituency-by-postal-code__warning';
    this.$resultWarning.hidden = true;

    this.$resultActions = document.createElement('div');
    this.$resultActions.className = 'constituency-by-postal-code__result-actions';
    this.$resultActions.hidden = true;

    const $reset = document.createElement('button');
    $reset.type = 'button';
    $reset.className = 'text-button constituency-by-postal-code__reset';

    const $resetIcon = document.createElement('span');
    $resetIcon.className = 'text-button__icon text-button__icon--left';
    $resetIcon.appendChild(createIcon(this.spriteUrl, 'arrow-left'));

    const $resetText = document.createElement('span');
    $resetText.className = 'text-button__text';
    const $resetTextInner = document.createElement('span');
    $resetTextInner.className = 'u-underline-focus';
    $resetTextInner.textContent = this.textReset;
    $resetText.appendChild($resetTextInner);

    $reset.append($resetIcon, $resetText);
    $reset.addEventListener('click', () => this.showSelection());
    this.$resultActions.appendChild($reset);

    this.$result.append(
      $successWrap,
      this.$resultWarning,
      this.$resultRepresentativesContainer,
      this.$resultActions,
    );

    this.append(this.$error, this.$selection, this.$result, this.queryMapAttribution());
  }

  private onPostalCodeInput = async (event: Event): Promise<void> => {
    const $postalCode = event.currentTarget as HTMLInputElement;
    const value = $postalCode.value.trim();

    if (!POSTAL_CODE_REGEX.test(value)) {
      this.controller?.abort();
      this.reset();
      return;
    }

    await this.fetchConstituency(value);
  };

  private cleanupMaps(): void {
    this.maps.forEach((map) => map.remove());
    this.maps = [];

    this.queryMapAttribution().setAttribute('hidden', 'hidden');
  }

  private reset(): void {
    this.cleanupMaps();
    this.results = [];

    this.$error.hidden = true;
    this.$selection.hidden = true;
    this.$selectionOptions.replaceChildren();
    this.$result.hidden = true;
    this.$resultRepresentativesContainer.hidden = true;
    this.$resultRepresentativesContainer.replaceChildren();
    this.$resultWarning.hidden = true;
    this.$resultWarning.textContent = '';
    this.$resultActions.hidden = true;
  }

  private showSelection(): void {
    this.$selectionOptions
      .querySelectorAll<HTMLInputElement>('input[type=radio]')
      .forEach(($input) => {
        $input.checked = false;
      });

    this.clearHiddenInputs();
    this.$result.hidden = true;
    this.$resultActions.hidden = true;
    this.$selection.hidden = false;
    this.queryMapAttribution().removeAttribute('hidden');
  }

  private renderSelection(): void {
    this.$selectionOptions.replaceChildren();
    this.cleanupMaps();

    if (this.results.length < 2) {
      this.$selection.hidden = true;
      return;
    }

    this.results.forEach((result, index) => {
      this.$selectionOptions.appendChild(this.createSelectionOption(result, index));
    });

    this.$selection.hidden = false;
  }

  private createSelectionOption(result: ConstituencyResult, index: number): HTMLDivElement {
    const $option = document.createElement('div');
    $option.className = 'constituency-by-postal-code__selection-option';

    const $radio = document.createElement('label');
    $radio.className = 'radio constituency-by-postal-code__selection-radio';

    const $input = document.createElement('input');
    $input.type = 'radio';
    $input.className = 'radio__input';
    $input.name = this.selectionName;
    $input.value = result.constituency.number.toString();
    $input.addEventListener('change', () => {
      if ($input.checked) {
        this.selectResult(index);
      }
    });

    const $marker = document.createElement('div');
    $marker.className = 'radio__marker';

    const $label = document.createElement('span');
    $label.className = 'radio__label constituency-by-postal-code__selection-heading';
    $label.textContent = `${result.constituency.number} – ${result.constituency.name}`;

    $radio.append($input, $marker, $label);
    $option.appendChild($radio);

    const $content = document.createElement('div');
    $content.className = 'constituency-by-postal-code__selection-content';

    if (result.geometry) {
      const $map = document.createElement('div');
      $map.className = 'constituency-by-postal-code__selection-map';
      $content.appendChild($map);
      this.attachMap($map, result.geometry).catch(() => {});
    }

    const photos = result.representatives.filter((rep) => rep.image);

    if (photos.length > 0) {
      const $photos = document.createElement('div');
      $photos.className = 'constituency-by-postal-code__selection-photos';

      photos.forEach((rep) => {
        const $photo = document.createElement('img');
        $photo.className = 'constituency-by-postal-code__selection-photo';
        $photo.src = rep.image!.src;
        $photo.alt = '';
        $photo.loading = 'lazy';
        if (rep.image!.srcset) {
          $photo.srcset = rep.image!.srcset;
        }
        $photo.title = `${rep.name} (${rep.party})`;
        $photos.appendChild($photo);
      });

      $content.appendChild($photos);
    }

    if ($content.hasChildNodes()) {
      $option.appendChild($content);
    }

    return $option;
  }

  private createSelectAllButton(): HTMLDivElement {
    const $actions = document.createElement('div');
    $actions.className = 'constituency-by-postal-code__selection-actions';

    const $button = document.createElement('button');
    $button.type = 'button';
    $button.className = 'button button--secondary button--small constituency-by-postal-code__select-all';
    $button.addEventListener('click', () => this.selectAllResults());

    const $text = document.createElement('span');
    $text.className = 'button__text';

    const $textInner = document.createElement('span');
    $textInner.className = 'u-underline-focus';
    $textInner.textContent = this.textSelectAll;
    $text.appendChild($textInner);

    $button.appendChild($text);
    $actions.appendChild($button);

    return $actions;
  }

  private async attachMap(container: HTMLElement, geometry: ConstituencyGeometry): Promise<void> {
    try {
      await import(/* webpackChunkName: "maplibre-gl" */ 'maplibre-gl/dist/maplibre-gl.css');

      const maplibre = await import(/* webpackChunkName: "maplibre-gl" */ 'maplibre-gl');
      const { Protocol, PMTiles } = await import(/* webpackChunkName: "maplibre-gl" */ 'pmtiles');
      const { layers, namedFlavor } = await import(/* webpackChunkName: "maplibre-gl" */ '@protomaps/basemaps');

      if (!container.isConnected) {
        return;
      }

      const protocol = new Protocol();
      maplibre.addProtocol('pmtiles', protocol.tile);

      const p = new PMTiles(`${this.tilesBaseUrl}/tiles.pmtiles`);
      protocol.add(p);

      const map = new maplibre.Map({
        container,
        style: {
          version: 8,
          glyphs: `${this.tilesBaseUrl}/fonts/{fontstack}/{range}.pbf`,
          sprite: `${this.tilesBaseUrl}/sprites/v4/light`,
          sources: {
            protomaps: {
              type: 'vector',
              url: `pmtiles://${this.tilesBaseUrl}/tiles.pmtiles`,
            },
          },
          layers: layers('protomaps', namedFlavor('light'), { lang: 'de' }),
        },
        attributionControl: false,
        cooperativeGestures: true,
      });

      this.maps.push(map);

      map.addControl(
        new maplibre.NavigationControl({
          showCompass: false,
          visualizePitch: false,
        }),
        'top-right',
      );

      map.on('load', () => {
        if (!container.isConnected) {
          map.remove();
          return;
        }

        map.addSource('constituency', {
          type: 'geojson',
          data: { type: 'Feature', geometry, properties: {} },
        });

        map.addLayer({
          id: 'constituency-fill',
          type: 'fill',
          source: 'constituency',
          paint: { 'fill-color': '#1a1a1a', 'fill-opacity': 0.2 },
        });

        map.addLayer({
          id: 'constituency-line',
          type: 'line',
          source: 'constituency',
          paint: { 'line-color': '#1a1a1a', 'line-width': 1.5 },
        });

        map.fitBounds(computeBounds(geometry), { padding: 12, animate: false });

        requestAnimationFrame(() => {
          map.setMaxBounds(map.getBounds());
        });
      });

      this.queryMapAttribution().removeAttribute('hidden');
    } catch {
      // Failing to load the map should not break the form.
    }
  }

  private readEmbeddedResults(): ConstituencyResult[] | null {
    const $script = this.querySelector<HTMLScriptElement>(
      'script.constituency-by-postal-code__results',
    );

    if (!$script) {
      return null;
    }

    $script.remove();

    try {
      const parsed = JSON.parse($script.textContent ?? '') as unknown;

      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed as ConstituencyResult[];
      }
    } catch {
      // Fall back to fetching when the embedded payload is invalid.
    }

    return null;
  }

  private readPrefilledConstituencies(): number[] {
    if (!this.name) {
      return [];
    }

    const selector = `input[name="${CSS.escape(`${this.name}[]`)}"]`;

    return Array.from(this.querySelectorAll<HTMLInputElement>(selector))
      .map(($input) => Number.parseInt($input.value, 10))
      .filter((value) => Number.isInteger(value));
  }

  private applyPrefilledSelection(): number {
    const prefilled = this.prefilledConstituencies;
    this.prefilledConstituencies = [];

    if (prefilled.length === 0) {
      return -1;
    }

    const matchedIndices = this.results.reduce<number[]>((indices, result, index) => {
      if (prefilled.includes(result.constituency.number)) {
        indices.push(index);
      }

      return indices;
    }, []);

    if (matchedIndices.length === 0) {
      return -1;
    }

    if (matchedIndices.length === this.results.length && this.results.length > 1) {
      this.selectAllResults();

      return matchedIndices[0];
    }

    const index = matchedIndices[0];

    const $input = this.$selectionOptions.querySelectorAll<HTMLInputElement>(
      'input[type=radio]',
    )[index];

    if ($input) {
      $input.checked = true;
    }

    this.selectResult(index);

    return index;
  }

  private clearHiddenInputs(): void {
    this.querySelectorAll('input[type=hidden]').forEach(($hiddenInput) => {
      $hiddenInput.remove();
    });
  }

  private selectResult(index: number): void {
    const result = this.results[index];

    if (!result) {
      return;
    }

    this.applySelection([result]);
  }

  private selectAllResults(): void {
    this.applySelection(this.results);
  }

  private applySelection(results: ConstituencyResult[]): void {
    if (results.length === 0) {
      return;
    }

    this.clearHiddenInputs();

    results.forEach((result) => {
      const $hiddenInput = document.createElement('input');
      $hiddenInput.type = 'hidden';
      $hiddenInput.name = `${this.name}[]`;
      $hiddenInput.value = result.constituency.number.toString();

      this.prepend($hiddenInput);
    });

    this.renderResults(results);

    if (this.results.length > 1) {
      this.$selection.hidden = true;
      this.$resultActions.hidden = false;
    } else {
      this.$resultActions.hidden = true;
    }

    this.queryMapAttribution().setAttribute('hidden', 'hidden');
  }

  private renderResults(results: ConstituencyResult[]): void {
    this.$successText.textContent = results
      .map((result) => renderTemplate(this.textSuccess, {
        name: result.constituency.name,
        number: result.constituency.number,
      }))
      .join(', ');

    const hasRepresentatives = results.some((result) => result.representatives.length > 0);

    this.$resultRepresentativesContainer.innerHTML = hasRepresentatives
      ? results.map((result) => result.representativesHtml).join('')
      : '';
    this.$resultRepresentativesContainer.hidden = !hasRepresentatives;

    const warnings = results
      .map((result) => result.warning)
      .filter((warning): warning is string => warning !== null);

    this.$resultWarning.replaceChildren();
    warnings.forEach((warning) => {
      const $warning = document.createElement('p');
      $warning.textContent = warning;
      this.$resultWarning.appendChild($warning);
    });
    this.$resultWarning.hidden = warnings.length === 0;

    this.$result.hidden = false;
  }

  private applyResults(constituencies: ConstituencyResult[]): void {
    this.reset();

    if (constituencies.length === 0) {
      this.$error.hidden = false;
      this.clearHiddenInputs();
      return;
    }

    this.results = constituencies;
    this.renderSelection();

    const prefilledIndex = this.applyPrefilledSelection();

    if (prefilledIndex === -1 && this.results.length === 1) {
      this.selectResult(0);
    }
  }

  private async fetchConstituency(postalCode: string): Promise<void> {
    const { apiEndpoint } = this.dataset;

    if (!apiEndpoint) {
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();

    const body = new URLSearchParams();
    body.append('postalCode', postalCode);

    try {
      const response = await fetch(apiEndpoint, {
        body,
        headers: { Accept: 'application/json' },
        method: 'POST',
        signal: this.controller.signal,
      });

      const data = await response.json() as ApiResponse;

      const ok = response.ok && data.success !== false;
      this.applyResults(ok ? data.constituencies ?? [] : []);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      this.reset();
      this.$error.hidden = false;
    }
  }
}

customElements.define('gff-constituency-by-postal-code', ConstituencyByPostalCodeElement);

declare global {
  interface HTMLElementTagNameMap {
    'gff-constituency-by-postal-code': ConstituencyByPostalCodeElement;
  }
}
